import { gql } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import { CREATION_ID } from '../../editor/editorReducer';
import type { ReactionThreadCompact_ThreadFragment } from '../../graphql/generated/graphqlTypes';
import usePostLayoutSize from '../usePostLayoutSize';
import CommentThreadCompactAvatarStack from './CommentThreadCompactAvatarStack';
import showFullPageSheet from './showFullPageSheet';

const getAvatarSummary = (thread: ReactionThreadCompact_ThreadFragment) => {
  const reactions = thread.reactions.map(({ author, emoji }) => ({
    author,
    emoji,
  }));

  if (reactions.length <= 3) {
    return reactions;
  }

  const [reaction, ...rest] = reactions;

  return [
    reaction,
    reactions.length - 2,
    _.findLast(rest, ({ emoji }) => emoji !== reaction?.emoji) || _.last(rest),
  ];
};

const THREAD_FRAGMENT = gql`
  fragment ReactionThreadCompact_thread on Thread {
    id
    reactions {
      author {
        ...CommentThreadCompactAvatarStack_user
      }
      emoji
    }
  }
  ${CommentThreadCompactAvatarStack.fragments.user}
`;

interface ReactionThreadCompactProps {
  thread: ReactionThreadCompact_ThreadFragment;
  isFocused: boolean;
}

const ReactionThreadCompact = ({
  thread,
  isFocused,
}: ReactionThreadCompactProps) => {
  const postLayoutSize = usePostLayoutSize();
  const isNarrow = ['mobile', 'narrow'].includes(postLayoutSize);
  const currentUser = useAppSelector((state) => state.session.user);

  const avatarStackItems =
    thread.id === CREATION_ID
      ? [{ author: currentUser }]
      : _.compact(getAvatarSummary(thread));

  const hasRing = isFocused && showFullPageSheet();

  return (
    <CommentThreadCompactAvatarStack
      items={isNarrow ? avatarStackItems.slice(0, 1) : avatarStackItems}
      ring={hasRing}
    />
  );
};

ReactionThreadCompact.fragments = { thread: THREAD_FRAGMENT };

export default ReactionThreadCompact;
